import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { $apiV1 } from "../../../../config/api";
import {
  ParametersTabWrapper,
} from "./EventSharedStyles";
import CollapsibleFilterSection from "./CollapsibleFilterSection";

export const FALLBACK_SEQUENCE_CONTEXT_FILTERS = [
  { id: "possession_ending_shot", label: "Ending in shot", group: "Outcomes" },
  { id: "possession_ending_goal", label: "Ending in goal", group: "Outcomes" },
  { id: "possession_ending_cross", label: "Ending in cross", group: "Outcomes" },
  { id: "possession_ending_possession_change", label: "Ending in possession change", group: "Outcomes" },
  { id: "possession_box_entry", label: "Ending in box entry", group: "Outcomes" },
  { id: "possession_final_third_entry", label: "Ending in final third", group: "Outcomes" },
  { id: "transition_ending_shot", label: "Transition to shot", group: "Outcomes" },
  { id: "transition_ending_goal", label: "Transition to goal", group: "Outcomes" },

  { id: "counter_attack_involvement", label: "Counter attack", group: "Attacks" },
  { id: "fast_attack_involvement", label: "Fast attack", group: "Attacks" },
  { id: "direct_attack_involvement", label: "Direct attack", group: "Attacks" },
  { id: "build_up_from_goalkeeper", label: "Build-up from GK", group: "Attacks" },
  { id: "build_up_from_own_third", label: "Build up from own third", group: "Attacks" },
  { id: "build_up_to_final_third", label: "Build-up to final third", group: "Attacks" },
  { id: "build_up_to_box", label: "Build-up to box", group: "Attacks" },
  { id: "central_build_up", label: "Central build-up", group: "Attacks" },
  { id: "wide_build_up", label: "Wide build-up", group: "Attacks" },
  { id: "failed_build_up", label: "Failed build-up", group: "Attacks" },

  { id: "pass_into_box", label: "Pass into box", group: "Progression" },
  { id: "carry_into_box", label: "Carry into box", group: "Progression" },
  { id: "half_space_progression", label: "Half-space progression", group: "Progression" },
  { id: "shot_creation_chain", label: "Shot creation chain", group: "Progression" },
  { id: "high_pressing", label: "High pressing", group: "Pressing types" },
  { id: "mid_pressing", label: "Mid pressing", group: "Pressing types" },
  { id: "low_block", label: "Low block", group: "Pressing types" },
  { id: "counter_pressing", label: "Counter pressing", group: "Pressing types" },

];

// Sequence contexts hidden from the team/player events UI regardless of whether
// they come from the API or the fallback list above.
const HIDDEN_SEQUENCE_CONTEXT_FILTER_IDS = new Set([
  "switch_of_play",
  "pre_assist",
  "retained_after_duel",
]);

const GroupTitle = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-family: "SatoshiMedium";
  font-size: 12px;
  line-height: 18px;
  padding: 10px 10px 2px;
`;

const Divider = styled.div`
  border-top: 1px solid ${(props) => props.theme.borderColor};
  margin: 4px 0;
`;

const groupFilters = (filters) =>
  filters.reduce((acc, filter) => {
    const group = filter.group || "Other";
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(filter);
    return acc;
  }, {});

const SequenceContextFilterPanel = ({
  selectedIds = [],
  onToggle,
  onClear,
  scope = "team",
  tourId,
}) => {
  const [apiFilters, setApiFilters] = useState([]);

  useEffect(() => {
    let isCancelled = false;

    const fetchFilters = async () => {
      try {
        const response = await $apiV1.get("/stats/sequence_context_filters", {
          params: { scope },
        });
        const results = response?.data?.results || [];
        if (!isCancelled && Array.isArray(results) && results.length > 0) {
          setApiFilters(results);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "test") {
          console.error("Error fetching sequence context filters:", error);
        }
      }
    };

    fetchFilters();

    return () => {
      isCancelled = true;
    };
  }, [scope]);

  const filters = (apiFilters.length > 0 ? apiFilters : FALLBACK_SEQUENCE_CONTEXT_FILTERS)
    .filter((filter) => !HIDDEN_SEQUENCE_CONTEXT_FILTER_IDS.has(filter.id));
  const groupedFilters = useMemo(() => groupFilters(filters), [filters]);
  const selectedLabels = useMemo(
    () =>
      filters
        .filter((filter) => selectedIds.includes(filter.id))
        .map((filter) => filter.label),
    [filters, selectedIds]
  );

  return (
    <CollapsibleFilterSection
      title="Sequence Context"
      emptySummary="Choose sequence context"
      selectedLabels={selectedLabels}
      tourId={tourId}
    >
      <ParametersTabWrapper>
        <div className="checkboxField">
          <label htmlFor={`sequence-context-${scope}-all`}>
            <input
              id={`sequence-context-${scope}-all`}
              type="checkbox"
              checked={selectedIds.length === 0}
              onChange={onClear}
            />
            All sequence contexts
          </label>
        </div>

        {Object.entries(groupedFilters).map(([group, groupItems]) => (
          <React.Fragment key={group}>
            <Divider />
            <GroupTitle>{group}</GroupTitle>
            {groupItems.map((filter) => (
              <div className="checkboxField" key={filter.id}>
                <label htmlFor={`sequence-context-${scope}-${filter.id}`}>
                  <input
                    id={`sequence-context-${scope}-${filter.id}`}
                    type="checkbox"
                    checked={selectedIds.includes(filter.id)}
                    onChange={() => onToggle(filter.id)}
                  />
                  {filter.label}
                </label>
              </div>
            ))}
          </React.Fragment>
        ))}
      </ParametersTabWrapper>
    </CollapsibleFilterSection>
  );
};

export default SequenceContextFilterPanel;
